import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import './ColorPreview.css';
import './TilePreview.css';
import {
  useAllItems,
  useConversation,
  type TransitColor,
  type ConversationDispatch,
  type SpecificConversationItem,
  type ColorValue,
} from '@/services/ConversationService';
import {
  ActionButton,
  Column,
  HoverElement,
  HoverElementBoundary,
  IconButton,
  PaletteInput,
  Row,
  type IconProps,
} from './DesignSystem';
import { useVersions } from './VersionsPreview';
import { TileHeader } from './TileHeader';

type ColorHex = string;

export type ColorID = string;
export function NewColorID(): ColorID {
  return crypto.randomUUID();
}

export function PaletteColorToTransit(color: PaletteColor): TransitColor {
  if (color.darkMode === undefined) {
    return { type: 'lightOnly', lightColorHex: color.lightMode ?? '' };
  }

  return {
    type: 'lightAndDark',
    lightColorHex: color.lightMode ?? '',
    darkColorHex: color.darkMode ?? '',
  };
}

export type PaletteColor = {
  name: string;
  lightMode?: ColorHex;
  darkMode?: ColorHex;
  id: ColorID;
};
export type ColorPreviewProps = {
  color: PaletteColor;
  equalSizedTiles: boolean;
};

type ColorPreviewReducerState = {
  isDarkMode: boolean;
  isInSlate: boolean;
};
type ColorPreviewReducerAction =
  | { type: 'setDarkMode'; isDarkMode: boolean }
  | { type: 'setIsInSlate'; isInSlate: boolean };

type ColorPreviewReducerDispatch = (action: ColorPreviewReducerAction) => void;

type ColorPreviewContextProps = {
  state: ColorPreviewReducerState;
  dispatch: ColorPreviewReducerDispatch;
  dispatchHelpers: ColorPreviewDispatchHelpers;
};

// The `ColorPreviewComputedState` is computed based on the state of the `ConversationService`
type ColorPreviewComputedState = {
  colors: PaletteColor[];
  /*
 `isEmptyPalette` indicates that the palette has not yet been populated with good color values, and so components should not render those values yet.
 The names of the colors are important, so still present empty tiles to the user. Then the user can work with Codi to fill in particular values.
 */
  isEmptyPalette: boolean;

  // `isLoading` indicates the color preview is loading. Currently, it always loads when `ConversationState`
  // is loading. In the future, loading will be on a per-palette basis.
  isLoading: boolean;
};

type ColorPreviewExternalContextProps = {
  state: ColorPreviewReducerState & ColorPreviewComputedState;
  dispatch: ColorPreviewReducerDispatch;
  dispatchHelpers: ColorPreviewDispatchHelpers;
  resolveColor: (_: ColorValue | null) => string | undefined;
};

export function resolveColor({
  color,
  colors,
}: {
  color: ColorValue | null;
  colors: PaletteColor[];
}): string | undefined {
  if (color === null) {
    return undefined;
  }

  switch (color.type) {
    case 'value':
      return color.value.colorHex;
    case 'reference':
      // TODO(LAB-304): Respect dark mode
      return colors.find((c) => c.name === color.reference)?.lightMode;
  }
}

export type GridSize = {
  gridColumn: string;
  gridRow: string;
};

export function ColorGridSize(name: string): GridSize {
  if (name.includes('action')) {
    return {
      gridColumn: '2 span',
      gridRow: '2 span',
    };
  }

  if (name.includes('background')) {
    return {
      gridColumn: '2 span',
      gridRow: '1 span',
    };
  }

  if (name.length > 14) {
    return {
      gridColumn: '2 span',
      gridRow: '1 span',
    };
  }

  return {
    gridColumn: '1 span',
    gridRow: '1 span',
  };
}

export const ColorPreviewContext =
  createContext<ColorPreviewContextProps | null>(null);

function TransitColorToPalette({
  id,
  name,
  color,
}: {
  id: ColorID;
  name: string;
  color: TransitColor;
}): PaletteColor {
  switch (color.type) {
    case 'lightOnly':
      return { name, lightMode: color.lightColorHex, id };
    case 'lightAndDark':
      return {
        name,
        lightMode: color.lightColorHex,
        darkMode: color.darkColorHex,
        id,
      };
  }
}

export function useColorPreview(): ColorPreviewExternalContextProps {
  const { state: conversationState } = useConversation();
  const context = useContext(ColorPreviewContext);
  if (context === null) {
    throw new Error(
      'useColorPreview must be used within a ColorPreviewBoundary',
    );
  }
  const { state, dispatch, dispatchHelpers } = context;
  const {
    state: { activeVersionID },
  } = useVersions();
  const allItems = useAllItems({ activeVersionID });

  // TODO(LAB-317): This normalization should be done upstream of the client
  function normalizeColor(color: PaletteColor): PaletteColor {
    // The format is #RRGGBB, i.e. 7 characters. Drop any extra characters,
    // e.g. alpha channel
    function ensureNoAlphaChannel(colorValue?: string): string | undefined {
      return colorValue?.slice(0, 7);
    }

    return {
      ...color,
      lightMode: ensureNoAlphaChannel(color.lightMode?.toUpperCase()),
      darkMode: ensureNoAlphaChannel(color.darkMode?.toUpperCase()),
    };
  }

  const colors = useMemo(() => {
    return getColorsFromConversation(allItems).map(normalizeColor);
  }, [allItems]);

  const computedState: ColorPreviewComputedState = {
    colors,
    isEmptyPalette: conversationState.type === 'new' && !state.isInSlate,
    isLoading: conversationState.loading !== undefined && !state.isInSlate,
  };
  return {
    state: { ...state, ...computedState },
    dispatch,
    dispatchHelpers,
    resolveColor: (color) => resolveColor({ color, colors }),
  };
}

function getColorsFromConversation(items: ConversationItem[]): PaletteColor[] {
  const colorsByName = new Map<string, PaletteColor>();
  let nameOrdering: string[] = [];

  function handleDefineColor(item: SpecificConversationItem<'defineColor'>) {
    if (colorsByName.get(item.name) === undefined) {
      nameOrdering = nameOrdering.concat(item.name);
    }
    colorsByName.set(
      item.name,
      TransitColorToPalette({
        id: item.id,
        name: item.name,
        color: item.color,
      }),
    );
  }

  function handleRemoval(name: string) {
    colorsByName.delete(name);
    nameOrdering = nameOrdering.filter((n) => n !== name);
  }

  items.forEach((item) => {
    switch (item.type) {
      case 'defineColor':
        handleDefineColor(item);
        break;
      case 'removeColor':
        handleRemoval(item.name);
        break;
      case 'updateColorPalette':
        item.removals.forEach((removal) => {
          const name = typeof removal === 'string' ? removal : removal.name;
          handleRemoval(name);
        });
        item.additions.forEach(handleDefineColor);
        break;
    }
  });

  return nameOrdering
    .map((name) => colorsByName.get(name))
    .filter((c) => c !== undefined);
}

export function useDarkMode(): { isDarkMode: boolean } {
  return useColorPreview().state;
}

export function Spacer() {
  return <div style={{ flexGrow: '1' }}></div>;
}

export type ColorPreviewDispatchHelpers = {
  addColor: ({ color }: { color: PaletteColor }) => void;

  changeColor: ({
    priorName,
    color,
  }: {
    priorName: string;
    color: PaletteColor;
  }) => void;

  removeColor: ({ name }: { name: string }) => void;
};

function MakeColorPreviewDispatchHelpers({
  dispatch,
}: {
  dispatch: ConversationDispatch;
}): ColorPreviewDispatchHelpers {
  return {
    addColor: function ({ color }: { color: PaletteColor }) {
      dispatch({ type: 'add-color', color });
    },
    changeColor: function ({
      priorName,
      color,
    }: {
      priorName: string;
      color: PaletteColor;
    }) {
      dispatch({ type: 'change-color', color, priorName });
    },
    removeColor: function ({ name }: { name: string }) {
      dispatch({ type: 'remove-color', name });
    },
  };
}

export function ColorPreviewBoundary({ children }: React.PropsWithChildren) {
  const { dispatch: conversationDispatch } = useConversation();
  const dispatchHelpers = MakeColorPreviewDispatchHelpers({
    dispatch: conversationDispatch,
  });

  function reducer(
    state: ColorPreviewReducerState,
    action: ColorPreviewReducerAction,
  ): ColorPreviewReducerState {
    switch (action.type) {
      case 'setDarkMode':
        return { ...state, isDarkMode: action.isDarkMode };
      case 'setIsInSlate':
        return {
          ...state,
          isInSlate: action.isInSlate,
        };
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    isDarkMode: false,
    isInSlate: false,
  });
  return (
    <ColorPreviewContext.Provider value={{ state, dispatch, dispatchHelpers }}>
      {children}
    </ColorPreviewContext.Provider>
  );
}

export function ColorMosaicPreview({
  color,
  equalSizedTiles,
}: ColorPreviewProps) {
  const { state, dispatchHelpers } = useColorPreview();
  const { isDarkMode } = useDarkMode();

  const [locked, setLocked] = useState<boolean>(false);

  const backgroundColor = useMemo(() => {
    if (isDarkMode) {
      return color.darkMode;
    }

    return color.lightMode;
  }, [isDarkMode, color]);
  const [draftColorValue, setDraftColorValue] = useState(backgroundColor ?? '');

  useEffect(() => {
    setDraftColorValue(backgroundColor ?? '');
  }, [backgroundColor, color.id, isDarkMode]);

  const updateColor = (newColor: PaletteColor) => {
    dispatchHelpers.changeColor({ color: newColor, priorName: color.name });
  };

  const commitDraftColorValue = (colorValue: string) => {
    const currentColorValue = backgroundColor ?? '';
    if (colorValue === currentColorValue) {
      return;
    }

    const updatedColor: PaletteColor = isDarkMode
      ? { ...color, darkMode: colorValue }
      : { ...color, lightMode: colorValue };

    updateColor(updatedColor);
  };

  const gridSize = useMemo(() => {
    return equalSizedTiles
      ? { gridRow: '1 span', gridColumn: '1 span' }
      : ColorGridSize(color.name);
  }, [color.name, equalSizedTiles]);

  const showsDarkModeError = useMemo(() => {
    const badDarkModeValue =
      color.darkMode === undefined || color.darkMode.length === 0;
    return isDarkMode && badDarkModeValue;
  }, [isDarkMode, color.darkMode]);

  function determineBackgroundColorToRender(): string {
    if (state.isEmptyPalette) {
      return '#ffffff80';
    }

    if (backgroundColor === undefined) {
      return '#ffffff80';
    }

    if (!backgroundColor.startsWith('#')) {
      return '#ffffff80';
    }

    // A hex color like #012 is the same as #001122
    if (backgroundColor.length === 4) {
      const r = backgroundColor.at(1) ?? '';
      const g = backgroundColor.at(2) ?? '';
      const b = backgroundColor.at(3) ?? '';
      return '#' + r + r + g + g + b + b;
    }

    // When a hex color has a length less than 7 (i.e., less than 6 hex digits)
    // then we don't have a valid color by itself. It renders as if the
    // remaining hex digits were 'F'. So, fill out those missing digits!
    if (backgroundColor.length < 7) {
      const missingFCount = 7 - backgroundColor.length;
      let adjustedBackgroundColor = backgroundColor;
      for (let i = 0; i < missingFCount; i++) {
        adjustedBackgroundColor += 'f';
      }
      return adjustedBackgroundColor;
    }

    return backgroundColor;
  }

  const renderedBackgroundColor = determineBackgroundColorToRender();

  // TODO(LAB-240): Style the text colors to work based on the `backgroundColor`
  const textColor: string = (() => {
    if (!renderedBackgroundColor.startsWith('#')) {
      // Default to black
      return '#000000';
    }

    const number = Number('0x' + renderedBackgroundColor.substring(1));
    if (Number.isNaN(number)) {
      // Default to black
      return '#000000';
    }

    // If number is more than half way to 0xFFFFFF
    const closerToBlack = number <= 0xffffff / 2;
    if (closerToBlack) {
      return '#ffffff';
    }

    return '#000000';
  })();

  const previewMarginBlockStart = state.isInSlate ? 8 : 24;
  const inputMarginInlineStart = state.isInSlate ? 8 : 16;

  // TODO: Consider lock action
  const enableLockAction = false;

  // The `shape` of the palette is the set of entity names in the palette.
  // During OOBE we don't want people to remove or rename colors (TBD),
  // so we "lock" the shape. This means they can't edit names, or remove colors.
  // TODO: Consider what editing we want to expose during OOBE, and if/when we want to "unlock" the shape
  const shapeOfPaletteIsLocked = true;

  return (
    <Row
      className={'tile-preview' + (locked ? ' locked-tile-preview' : '')}
      style={{
        ...gridSize,
        overflow: 'hidden',
        backgroundColor: renderedBackgroundColor,
        width: '100%',
        height: '100%',
      }}
    >
      <HoverElementBoundary>
        <form
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
          onSubmit={(e) => {
            e.preventDefault();
            commitDraftColorValue(draftColorValue);
          }}
        >
          <Column style={{ height: '100%', width: '100%' }}>
            <TileHeader>
              <PaletteInput
                value={color.name}
                placeholder="Color Name"
                onChange={(name) => {
                  updateColor({ ...color, name });
                }}
                type="text"
                fillWidth={true}
                disabled={shapeOfPaletteIsLocked}
                style={{
                  marginInlineStart: inputMarginInlineStart,
                  background: 'none',
                  border: 'none',
                  paddingBlock: undefined,
                  // TODO: Show the full text in a tool-tip on hover
                  textOverflow: 'ellipsis',
                }}
              />

              <Spacer />
              {shapeOfPaletteIsLocked || (
                <ActionButton
                  action="remove"
                  color={textColor}
                  hidden={state.isEmptyPalette}
                  onClick={() => {
                    dispatchHelpers.removeColor({ name: color.name });
                  }}
                />
              )}
            </TileHeader>
            <Spacer />
            <Row
              style={{
                marginBlockEnd: previewMarginBlockStart,
              }}
            >
              {state.isEmptyPalette || (
                <PaletteInput
                  value={draftColorValue}
                  placeholder="Color Value"
                  onChange={(value) => {
                    setDraftColorValue(value);
                  }}
                  onBlur={() => {
                    commitDraftColorValue(draftColorValue);
                  }}
                  type="text"
                  style={{
                    marginInlineStart: inputMarginInlineStart,
                    // TODO: It'd be neato to know how wide to make this to fit a color code
                    width: 100,
                  }}
                />
              )}
              <Spacer />
              {enableLockAction && (
                <ActionButton
                  action={locked ? 'locked' : 'unlocked'}
                  color={textColor}
                  hidden={state.isEmptyPalette}
                  onClick={() => {
                    setLocked(!locked);
                  }}
                />
              )}
            </Row>
          </Column>
        </form>
      </HoverElementBoundary>
    </Row>
  );
}
